import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const LS_UNLOCKED = 'ea_unlocked';
const LS_CODE = 'ea_code';
const LS_CAMPAIGN = 'ea_campaign';
const LS_INFLUENCER = 'ea_influencer';
const LS_SESSION_ID = 'ea_session_id';
const LS_UNLOCKS_V2 = 'ea_unlocks_v2';

/** @param {unknown} v */
function normalizeCampaign(v) {
    return String(v || '')
        .trim()
        .toLowerCase();
}

/** @param {unknown} v */
function normalizeMode(v) {
    const mode = String(v || '')
        .trim()
        .toLowerCase();
    return mode === 'code' || mode === 'loyalty' || mode === 'either' ? mode : '';
}

/** @returns {HTMLElement | null} */
function getPrimaryUnlockRoot() {
    const el = document.querySelector('[data-ea-unlock]');
    return el instanceof HTMLElement ? el : null;
}

/** @param {HTMLElement | null} root @param {string} key */
function getUnlockDatasetValue(root, key) {
    return root?.dataset?.[key] || document.body?.dataset?.[key] || '';
}

/** @returns {{ campaign: string, collectionHandle: string, context: string, mode: string }} */
function getPageUnlockScope() {
    const root = getPrimaryUnlockRoot();
    if (!root) {
        return {
            campaign: document.body?.dataset?.eaCampaign || '',
            collectionHandle: document.body?.dataset?.eaCollection || '',
            context: document.body?.dataset?.eaContext || '',
            mode: document.body?.dataset?.eaMode || '',
        };
    }
    return {
        campaign: root.dataset.eaCampaign || document.body?.dataset?.eaCampaign || '',
        collectionHandle: root.dataset.eaCollection || '',
        context: root.dataset.eaContext || '',
        mode: root.dataset.eaMode || document.body?.dataset?.eaMode || '',
    };
}

/** @returns {Record<string, { code?: string, campaign?: string, influencer?: string, unlockedAt?: number }>} */
function getUnlockMap() {
    try {
        const raw = localStorage.getItem(LS_UNLOCKS_V2) || '';
        const json = raw ? JSON.parse(raw) : null;
        return json && typeof json === 'object' ? json : {};
    } catch (_e) {
        return {};
    }
}

/** @param {Record<string, any>} map */
function setUnlockMap(map) {
    try {
        localStorage.setItem(LS_UNLOCKS_V2, JSON.stringify(map || {}));
    } catch (_e) {
        // ignore
    }
}

/**
 * @typedef {Object} UnlockState
 * @property {boolean} unlocked
 * @property {boolean} loyaltyUnlocked
 * @property {boolean} codeUnlocked
 * @property {string} code
 * @property {string} campaign
 * @property {string} influencer
 * @property {string} loyaltyStatus
 */

/** @param {string} name */
function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')}=([^;]*)`));
    return match && match[1] ? decodeURIComponent(match[1]) : null;
}

/** @param {string} name @param {string} value @param {number} [days] */
function setCookie(name, value, days = 30) {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

function getOrCreateSessionId() {
    const existing = localStorage.getItem(LS_SESSION_ID);
    if (existing) return existing;
    const sid = (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    localStorage.setItem(LS_SESSION_ID, sid);
    return sid;
}

/** @returns {UnlockState} */
function getUnlockState(scopeCampaign = '', scopeMode = '') {
    const scopeKey = normalizeCampaign(scopeCampaign);
    const mode = normalizeMode(scopeMode || document.body?.dataset?.eaMode || '');
    const root = getPrimaryUnlockRoot();
    let loyaltyUnlocked =
        getUnlockDatasetValue(root, 'eaLoyaltyMember') === 'true' ||
        getUnlockDatasetValue(root, 'eaLoyaltyVip') === 'true' ||
        getUnlockDatasetValue(root, 'eaLoyaltyTier') === 'VIP';
    let loyaltyStatus = getUnlockDatasetValue(root, 'eaLoyaltyStatus');
    const codeUnlocked = localStorage.getItem(LS_UNLOCKED) === '1' || getCookie('ea_unlocked') === '1';

    // Campaign-scoped unlocks (V2):
    // If the page declares a campaign, only treat the user as unlocked if *that campaign* is unlocked.
    // This prevents a previous event code from carrying over to a different event.
    const unlockMap = getUnlockMap();
    const scoped = scopeKey ? unlockMap[scopeKey] : null;

    // Back-compat migration:
    // If user has the old global unlock + a stored campaign that matches this page's campaign,
    // write it into the v2 map so future loads are correctly scoped.
    const legacyCode = localStorage.getItem(LS_CODE) || getCookie('ea_code') || '';
    const legacyCampaign = localStorage.getItem(LS_CAMPAIGN) || getCookie('ea_campaign') || '';
    const legacyInfluencer = localStorage.getItem(LS_INFLUENCER) || '';
    const legacyKey = normalizeCampaign(legacyCampaign);
    if (mode !== 'loyalty' && scopeKey && !scoped && codeUnlocked && legacyKey && legacyKey === scopeKey) {
        unlockMap[scopeKey] = {
            code: legacyCode || '',
            campaign: legacyCampaign || scopeCampaign || '',
            influencer: legacyInfluencer || '',
            unlockedAt: Date.now(),
        };
        setUnlockMap(unlockMap);
    }

    let effectiveCodeUnlocked = codeUnlocked;
    let code = legacyCode;
    let campaign = legacyCampaign;
    const influencer = localStorage.getItem(LS_INFLUENCER) || '';

    if (scopeKey) {
        const v2 = getUnlockMap()[scopeKey];
        const v2Unlocked = Boolean(v2 && (v2.code || v2.influencer));
        effectiveCodeUnlocked = v2Unlocked;
        code = (v2 && v2.code) || '';
        campaign = (v2 && v2.campaign) || scopeCampaign || '';
        // Note: keep "codeUnlocked" as the legacy/global indicator; scoped unlock uses v2Unlocked.
        // This prevents breaking other logic that expects codeUnlocked to exist.
    }

    let unlocked = loyaltyUnlocked || effectiveCodeUnlocked;
    if (mode === 'loyalty') {
        unlocked = loyaltyUnlocked;
        code = '';
        campaign = scopeCampaign || campaign || '';
    } else if (mode === 'code') {
        unlocked = effectiveCodeUnlocked;
        // Code-only campaigns unlock strictly by code. A logged-in loyalty member must not
        // also stamp loyalty line properties/attributes on top of the code unlock.
        loyaltyUnlocked = false;
        loyaltyStatus = '';
    } else if (mode === 'either') {
        unlocked = loyaltyUnlocked || effectiveCodeUnlocked;
    }

    return { unlocked, loyaltyUnlocked, codeUnlocked: effectiveCodeUnlocked, code, campaign, influencer, loyaltyStatus };
}

/** @param {{ code: string, campaign?: string, influencer?: string }} param0 */
function setUnlockState({ code, campaign, influencer }) {
    localStorage.setItem(LS_UNLOCKED, '1');
    localStorage.setItem(LS_CODE, code);
    setCookie('ea_unlocked', '1');
    setCookie('ea_code', code);

    if (campaign) {
        localStorage.setItem(LS_CAMPAIGN, campaign);
        setCookie('ea_campaign', campaign);
    }

    if (influencer) {
        localStorage.setItem(LS_INFLUENCER, influencer);
    }

    // V2: also persist a campaign-scoped unlock so future events don't inherit a previous unlock.
    const key = normalizeCampaign(campaign);
    if (key) {
        const map = getUnlockMap();
        map[key] = {
            code,
            campaign,
            influencer: influencer || '',
            unlockedAt: Date.now(),
        };
        setUnlockMap(map);
    }
}

function setBodyUnlockClasses() {
    const scope = getPageUnlockScope();
    const { unlocked } = getUnlockState(scope.campaign, scope.mode);
    document.body.classList.toggle('ea-unlocked', unlocked);
    document.body.classList.toggle('ea-locked', !unlocked);
}

function syncEarlyAccessLineItemProperties() {
    const scope = getPageUnlockScope();
    const state = getUnlockState(scope.campaign, scope.mode);
    const wrappers = document.querySelectorAll('[data-ea-required="1"]');
    wrappers.forEach((wrapper) => {
        if (!(wrapper instanceof HTMLElement)) return;
        const forms = wrapper.querySelectorAll('form[data-type="add-to-cart-form"]');
        forms.forEach((form) => {
            if (!(form instanceof HTMLFormElement)) return;
            const codeEl = form.querySelector('input[data-ea-line-prop="code"]');
            const accessLabelEl = form.querySelector('input[data-ea-line-prop="access-label"]');
            const campaignEl = form.querySelector('input[data-ea-line-prop="campaign"]');
            const methodEl = form.querySelector('input[data-ea-line-prop="method"]');
            const loyaltyStatusEl = form.querySelector('input[data-ea-line-prop="loyalty-status"]');
            if (codeEl instanceof HTMLInputElement) {
                // Loyalty unlocks are not codes, so only write real code-based unlock values.
                codeEl.value = state.code || '';
            }
            if (accessLabelEl instanceof HTMLInputElement) {
                accessLabelEl.value = state.loyaltyUnlocked ? 'SABO Resort Loyalty' : '';
            }
            if (campaignEl instanceof HTMLInputElement) {
                campaignEl.value = state.campaign || '';
            }
            if (methodEl instanceof HTMLInputElement) {
                methodEl.value = state.loyaltyUnlocked ? 'loyalty' : state.code ? 'code' : '';
            }
            if (loyaltyStatusEl instanceof HTMLInputElement) {
                loyaltyStatusEl.value = state.loyaltyUnlocked ? state.loyaltyStatus || 'enrolled' : '';
            }
        });
    });
}

function getCartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
}

async function removeEarlyAccessCartItemsIfLocked() {
    const scope = getPageUnlockScope();
    const state = getUnlockState(scope.campaign, scope.mode);
    if (state.unlocked) return;
    if (typeof Theme === 'undefined' || !Theme?.routes?.cart_change_url) return;

    /** @type {Array<{ line: number }>} */
    const rows = Array.from(document.querySelectorAll('[data-ea-cart-item="1"][data-ea-cart-line]'))
        .map((el) => {
            if (!(el instanceof HTMLElement)) return null;
            const line = parseInt(el.dataset.eaCartLine || '', 10);
            return Number.isFinite(line) && line > 0 ? { line } : null;
        })
        .filter((v) => v !== null);

    if (rows.length === 0) return;

    // Remove from bottom-up so line numbers shift less.
    const lines = Array.from(new Set(rows.map((r) => r.line))).sort((a, b) => b - a);
    const sections = getCartSectionIds();
    const primarySectionId = sections[0] || '';

    for (const line of lines) {
        const body = JSON.stringify({
            line,
            quantity: 0,
            sections: sections.join(','),
            sections_url: window.location.pathname,
        });

        const res = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const text = await res.text();
        let json = null;
        try {
            json = JSON.parse(text);
        } catch (_e) {
            // ignore
        }
        if (!json?.sections || !primarySectionId) continue;

        // Dispatch a cart update so cart drawer/page re-renders immediately.
        let itemCount = 0;
        try {
            const doc = new DOMParser().parseFromString(json.sections[primarySectionId], 'text/html');
            const newCount = doc.querySelector('[ref="cartItemCount"]')?.textContent;
            itemCount = newCount ? parseInt(newCount, 10) : 0;
        } catch (_e) {
            // ignore
        }

        document.dispatchEvent(
            new CartUpdateEvent({}, primarySectionId, {
                itemCount,
                source: 'early-access',
                sections: json.sections,
            })
        );
    }
}

function preventEarlyAccessAddToCartIfLocked() {
    document.addEventListener(
        'submit',
        (e) => {
            const scope = getPageUnlockScope();
            const state = getUnlockState(scope.campaign, scope.mode);
            if (state.unlocked) return;
            const form = e.target;
            if (!(form instanceof HTMLFormElement)) return;

            // Only gate product add-to-cart forms inside EA required wrappers.
            if (form.dataset.type !== 'add-to-cart-form') return;
            const wrapper = form.closest('[data-ea-required="1"]');
            if (!wrapper) return;

            e.preventDefault();
            e.stopPropagation();

            // Nudge user toward the unlock banner if present.
            const unlock = document.querySelector('[data-ea-unlock]');
            unlock?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        },
        true
    );
}

/** @param {{ code: string, campaign?: string, influencer?: string }} param0 */
async function updateCartAttributes({ code, campaign, influencer }) {
    if (typeof Theme === 'undefined' || !Theme?.routes?.cart_update_url) return;

    /**
     * Public cart attributes are shown on Shopify checkout. Campaign / influencer must use
     * private attribute names (suffix `__`) per Cart API; also clear legacy public keys.
     * @see https://shopify.dev/docs/api/ajax/reference/cart#private-cart-attributes
     */
    /** @type {Record<string, string>} */
    const attrs = {
        early_access_code: code || '',
        early_access_campaign: '',
        early_access_influencer: '',
    };
    if (campaign) attrs.early_access_campaign__ = campaign;
    if (influencer) attrs.early_access_influencer__ = influencer;

    const body = JSON.stringify({ attributes: attrs });
    await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));
}

/** @param {HTMLElement} root @param {string} code */
async function callProxyUnlock(root, code) {
    // IMPORTANT:
    // Some Shopify setups respond to POST /apps/early-access with a 301 redirect to the proxy URL.
    // Browsers often drop POST bodies on redirects, which causes "Missing code" backend outcomes.
    //
    // Use GET + query params so Shopify forwards them in the signed querystring reliably.
    const params = new URLSearchParams();
    params.set('code', code);
    params.set('campaign', root.dataset.eaCampaign || '');
    params.set('collectionHandle', root.dataset.eaCollection || '');
    params.set('shop', root.dataset.eaShop || Shopify?.shop || '');
    params.set('countryCode', root.dataset.eaCountry || '');
    params.set('sessionId', getOrCreateSessionId());
    // Avoid putting email/pageUrl in the querystring (PII/very long URLs).

    // Shopify App Proxy config:
    //   prefix: apps
    //   subpath: early-access
    //
    // Note: some stores only have the proxy registered for the base path (/apps/early-access),
    // and subpaths (e.g. /apps/early-access/unlock) can 404 at the Shopify edge.
    // We keep the unlock data in the querystring so it survives any redirect behavior.
    const url = `/apps/early-access?${params.toString()}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
    });

    let json = null;
    try {
        json = await res.json();
    } catch (_e) {
        // ignore
    }

    if (!res.ok || !json) {
        const msg = json?.error || json?.message || 'Unable to validate code. Please try again.';
        throw new Error(msg);
    }

    // App proxy uses ok=request succeeded, valid=code accepted.
    // A random string returns { ok: true, valid: false, message: "Invalid code." }.
    const hasValidFlag = Object.prototype.hasOwnProperty.call(json, 'valid');
    const accepted = hasValidFlag ? json.valid === true : json.ok === true;
    if (!accepted) {
        const msg = json.error || json.message || 'Invalid code.';
        throw new Error(msg);
    }

    return json;
}

/** @param {{ code: string, campaign?: string, collectionHandle?: string }} param0 */
function fireKlaviyoUnlockEvent({ code, campaign, collectionHandle }) {
    const learnq = window._learnq;
    if (!Array.isArray(learnq)) return;
    learnq.push([
        'track',
        'Early Access Unlocked',
        {
            code,
            campaign: campaign || '',
            collection_handle: collectionHandle || '',
            page_url: window.location.href,
        },
    ]);
}

/** @param {HTMLElement} root */
function mountUnlockForm(root) {
    const form = root.querySelector('[data-ea-unlock-form]');
    const msg = root.querySelector('[data-ea-message]');
    if (!(form instanceof HTMLFormElement) || !(msg instanceof HTMLElement)) return;
    const details = root.querySelector('.ea-unlock__details');
    const panel = root.querySelector('.ea-unlock__panel');
    const inner = panel instanceof HTMLElement ? panel.querySelector('.ea-unlock__panel-inner') : null;

    const syncPanelHeight = () => {
        if (!(panel instanceof HTMLElement)) return;
        // If closed, collapse immediately.
        if (!(details instanceof HTMLDetailsElement) || !details.open) {
            panel.style.setProperty('--ea-panel-max-height', '0px');
            return;
        }
        // If open, measure inner content height and animate to it.
        const h = (inner instanceof HTMLElement ? inner.scrollHeight : panel.scrollHeight) || 0;
        // Add a small buffer to avoid clipping from borders/sub-pixel rounding on different browsers.
        const bufferPx = 16;
        panel.style.setProperty('--ea-panel-max-height', `${h + bufferPx}px`);
    };

    if (details instanceof HTMLDetailsElement) {
        details.addEventListener('toggle', () => {
            // Wait a frame so layout reflects the new open state before measuring.
            requestAnimationFrame(syncPanelHeight);
        });
    }

    // Initialize height on mount.
    requestAnimationFrame(syncPanelHeight);

    // Keep the animation height accurate as content wraps/changes (mobile/desktop) or fonts load.
    const rafSync = () => requestAnimationFrame(syncPanelHeight);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && inner instanceof HTMLElement) {
        ro = new ResizeObserver(() => rafSync());
        ro.observe(inner);
    }

    const onResize = () => rafSync();
    window.addEventListener('resize', onResize, { passive: true });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const fd = new FormData(form);
        const rawCode = String(fd.get('code') || '').trim();
        const code = rawCode.toUpperCase();

        msg.hidden = true;
        msg.textContent = '';
        msg.removeAttribute('data-ea-status');

        if (!code) return;

        form.querySelector('button[type="submit"]')?.setAttribute('aria-busy', 'true');

        try {
            const resp = await callProxyUnlock(root, code);
            const campaign = resp.campaign || root.dataset.eaCampaign || '';
            const influencer = resp.influencer || '';

            setUnlockState({ code, campaign, influencer });
            syncEarlyAccessLineItemProperties();

            await updateCartAttributes({ code, campaign, influencer });

            fireKlaviyoUnlockEvent({
                code,
                campaign,
                collectionHandle: root.dataset.eaCollection || '',
            });

            msg.dataset.eaStatus = 'success';
            msg.textContent = 'Unlocked. Happy shopping.';
            msg.hidden = false;

            // Give the user a moment to see the success state before the banner collapses.
            window.setTimeout(() => {
                setBodyUnlockClasses();
            }, 450);
        } catch (err) {
            if (details instanceof HTMLDetailsElement) details.open = true;
            requestAnimationFrame(syncPanelHeight);
            msg.dataset.eaStatus = 'error';
            msg.textContent = err instanceof Error ? err.message : 'Invalid code.';
            msg.hidden = false;
        } finally {
            form.querySelector('button[type="submit"]')?.removeAttribute('aria-busy');
        }
    });

    // Best-effort cleanup if the banner is removed/re-rendered.
    const mo = new MutationObserver(() => {
        if (!document.body.contains(root)) {
            window.removeEventListener('resize', onResize);
            ro?.disconnect?.();
            mo.disconnect();
        }
    });
    mo.observe(document.body, { childList: true, subtree: true });
}

/**
 * iOS Safari auto-zooms the viewport when a text input is focused and its computed
 * font-size renders under 16px. The unlock input is already pinned to 16px in CSS, but
 * a stale/cached theme or a scaled ancestor can still trip the zoom. As a guaranteed
 * safeguard, momentarily lock zoom while the EA code field is focused, then restore the
 * original viewport (so pinch-zoom remains available everywhere else).
 */
function preventIosZoomOnUnlockInput() {
    const isTouchApple =
        typeof navigator !== 'undefined' &&
        /iP(hone|ad|od)/.test(navigator.platform || '') ||
        (typeof navigator !== 'undefined' &&
            (navigator.userAgent || '').includes('Mac') &&
            typeof document !== 'undefined' &&
            'ontouchend' in document);
    if (!isTouchApple) return;

    const viewport = document.querySelector('meta[name="viewport"]');
    if (!(viewport instanceof HTMLMetaElement)) return;

    const original = viewport.getAttribute('content') || 'width=device-width, initial-scale=1';
    const locked = /maximum-scale/.test(original) ? original : `${original}, maximum-scale=1`;

    /** @param {EventTarget | null} target */
    const isUnlockInput = (target) =>
        target instanceof HTMLElement && !!target.closest('.ea-unlock__input');

    const lock = () => viewport.setAttribute('content', locked);
    const restore = () => viewport.setAttribute('content', original);

    // touchstart fires before iOS decides whether to zoom on focus, so lock zoom as
    // early as possible; focusin covers keyboard/programmatic focus; focusout restores.
    document.addEventListener(
        'touchstart',
        (e) => {
            if (isUnlockInput(e.target)) lock();
        },
        { capture: true, passive: true }
    );
    document.addEventListener(
        'focusin',
        (e) => {
            if (isUnlockInput(e.target)) lock();
        },
        true
    );
    document.addEventListener(
        'focusout',
        (e) => {
            if (isUnlockInput(e.target)) restore();
        },
        true
    );
}

function init() {
    if (!document.body) return;

    // Set initial lock/unlock classes ASAP.
    setBodyUnlockClasses();
    preventIosZoomOnUnlockInput();
    preventEarlyAccessAddToCartIfLocked();
    syncEarlyAccessLineItemProperties();
    removeEarlyAccessCartItemsIfLocked().catch(() => { });

    // Keep cart attributes in sync (best-effort) if already unlocked.
    const scope = getPageUnlockScope();
    const state = getUnlockState(scope.campaign, scope.mode);
    if (state.unlocked && state.code) {
        updateCartAttributes({ code: state.code, campaign: state.campaign, influencer: state.influencer }).catch(() => { });
    } else if (state.unlocked && state.loyaltyUnlocked) {
        updateCartAttributes({ code: '', campaign: state.campaign, influencer: state.influencer }).catch(() => { });
    }

    // If forms are re-rendered (variant changes), re-apply EA properties.
    document.addEventListener(ThemeEvents.variantUpdate, () => syncEarlyAccessLineItemProperties());

    const roots = document.querySelectorAll('[data-ea-unlock]');
    roots.forEach((root) => {
        if (!(root instanceof HTMLElement)) return;
        mountUnlockForm(root);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
    init();
}


